<template>
	<div class="p-0 min-h-screen bg-gray-100">
		<div class="bg-white py-4 px-6 border-b border-gray-200">
			<h1 class="text-2xl font-semibold text-gray-900 m-0">Templates</h1>
		</div>

		<div class="p-6">
			<div class="bg-white rounded-lg shadow-sm">
				<div class="flex justify-between items-center py-4 px-6 border-b border-gray-200">
					<h2 class="text-lg font-semibold text-gray-900 m-0">All Templates</h2>
					<button
						class="bg-violet-600 hover:bg-violet-700 text-white border-none rounded-md py-2 px-4 text-sm font-medium cursor-pointer flex items-center"
						@click="showCreateModal = true"
					>
						<i class="fa fa-plus mr-1.5"></i> Add Template
					</button>
				</div>

				<div class="flex justify-between items-center py-4 px-6 gap-4 flex-wrap">
					<div class="flex-1 min-w-[200px] max-w-[400px]">
						<input
							v-model="searchQuery"
							type="text"
							placeholder="Search templates..."
							class="w-full py-2 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:border-violet-500 focus:ring-1 focus:ring-violet-500"
						>
					</div>
					<div class="flex items-center gap-2">
						<label class="text-sm text-gray-700">Show:</label>
						<select v-model="pageSize" class="py-2 px-3 border border-gray-300 rounded-md text-sm">
							<option :value="10">10</option>
							<option :value="25">25</option>
							<option :value="50">50</option>
							<option :value="100">100</option>
						</select>
					</div>
				</div>

				<div v-if="isLoading" class="py-12 text-center text-gray-500">Loading templates...</div>

				<div v-else class="overflow-x-auto">
					<table class="w-full border-collapse">
						<thead>
							<tr>
								<th class="py-3 px-6 text-left text-xs font-semibold text-gray-500 uppercase border-b border-gray-200 bg-gray-50">ID</th>
								<th class="py-3 px-6 text-left text-xs font-semibold text-gray-500 uppercase border-b border-gray-200 bg-gray-50">Name</th>
								<th class="py-3 px-6 text-left text-xs font-semibold text-gray-500 uppercase border-b border-gray-200 bg-gray-50">Width</th>
								<th class="py-3 px-6 text-left text-xs font-semibold text-gray-500 uppercase border-b border-gray-200 bg-gray-50">Height</th>
								<th class="py-3 px-6 text-left text-xs font-semibold text-gray-500 uppercase border-b border-gray-200 bg-gray-50">Transformable</th>
								<th class="py-3 px-6 text-left text-xs font-semibold text-gray-500 uppercase border-b border-gray-200 bg-gray-50">Dynamic</th>
								<th class="py-3 px-6 text-right text-xs font-semibold text-gray-500 uppercase border-b border-gray-200 bg-gray-50">Actions</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="item in paginatedItems" :key="item.id" class="border-b border-gray-200 hover:bg-gray-50">
								<td class="py-3 px-6 text-sm text-gray-900">{{ item.id }}</td>
								<td class="py-3 px-6 text-sm text-gray-900">{{ item.name }}</td>
								<td class="py-3 px-6 text-sm text-gray-900">{{ item.width ?? '-' }}</td>
								<td class="py-3 px-6 text-sm text-gray-900">{{ item.height ?? '-' }}</td>
								<td class="py-3 px-6 text-sm text-gray-900">
									<span :class="item.transformable ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'" class="py-0.5 px-2 rounded-full text-xs font-medium">
										{{ item.transformable ? 'Yes' : 'No' }}
									</span>
								</td>
								<td class="py-3 px-6 text-sm text-gray-900">
									<span :class="item.dynamic ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'" class="py-0.5 px-2 rounded-full text-xs font-medium">
										{{ item.dynamic ? 'Yes' : 'No' }}
									</span>
								</td>
								<td class="py-3 px-6 text-sm text-gray-900 text-right">
									<div class="flex gap-2 justify-end">
										<button
											class="bg-gray-100 hover:bg-gray-200 text-gray-700 border-none rounded py-1.5 px-2.5 cursor-pointer min-w-[32px] min-h-[32px] flex items-center justify-center"
											@click="editItem(item.id)"
											title="Edit"
										>
											<i class="fa fa-pencil"></i>
										</button>
										<button
											class="bg-red-50 hover:bg-red-100 text-red-600 border-none rounded py-1.5 px-2.5 cursor-pointer min-w-[32px] min-h-[32px] flex items-center justify-center"
											@click="confirmDelete(item)"
											title="Delete"
										>
											<i class="fa fa-trash"></i>
										</button>
									</div>
								</td>
							</tr>
							<tr v-if="paginatedItems.length === 0">
								<td colspan="7" class="py-12 px-6 text-center text-gray-500">No templates found</td>
							</tr>
						</tbody>
					</table>
				</div>

				<div class="flex justify-between items-center py-4 px-6 border-t border-gray-200 flex-wrap gap-4">
					<div class="text-sm text-gray-500">
						Showing {{ startRow }} to {{ endRow }} of {{ totalRows }} entries
					</div>
					<div class="flex items-center gap-2">
						<button class="bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 rounded py-1.5 px-3 text-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed" @click="goToPage(1)" :disabled="currentPage === 1">First</button>
						<button class="bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 rounded py-1.5 px-3 text-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed" @click="goToPage(currentPage - 1)" :disabled="currentPage === 1">Previous</button>
						<span class="text-sm text-gray-700 px-2">Page {{ currentPage }} of {{ totalPages }}</span>
						<button class="bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 rounded py-1.5 px-3 text-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed" @click="goToPage(currentPage + 1)" :disabled="currentPage === totalPages">Next</button>
						<button class="bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 rounded py-1.5 px-3 text-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed" @click="goToPage(totalPages)" :disabled="currentPage === totalPages">Last</button>
					</div>
				</div>
			</div>
		</div>

		<div v-if="showCreateModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000]" @click.self="showCreateModal = false">
			<div class="bg-white rounded-lg w-full max-w-[500px] m-4 shadow-xl">
				<div class="flex justify-between items-center py-4 px-6 border-b border-gray-200">
					<h3 class="text-lg font-semibold text-gray-900 m-0">Create New Template</h3>
					<button class="bg-transparent border-none text-2xl text-gray-500 hover:text-gray-700 cursor-pointer p-0 leading-none" @click="showCreateModal = false">&times;</button>
				</div>
				<div class="p-6">
					<div class="mb-4">
						<label class="block text-sm font-medium text-gray-700 mb-1">Name *</label>
						<input
							v-model="newTemplateName"
							type="text"
							placeholder="Enter template name"
							class="w-full py-2 px-3 border border-gray-300 rounded-md text-sm box-border focus:outline-none focus:border-violet-500 focus:ring-1 focus:ring-violet-500"
						>
					</div>
				</div>
				<div class="flex justify-end gap-3 py-4 px-6 border-t border-gray-200">
					<button
						class="bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 rounded-md py-2 px-4 text-sm font-medium cursor-pointer"
						@click="showCreateModal = false"
					>
						Cancel
					</button>
					<button
						class="bg-violet-600 hover:bg-violet-700 text-white border-none rounded-md py-2 px-4 text-sm font-medium cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
						@click="createItem"
						:disabled="isCreating || !newTemplateName.trim()"
					>
						{{ isCreating ? 'Creating...' : 'Create' }}
					</button>
				</div>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { TemplateModel } from '@sosocio/creator/interfaces';
import TemplateService from 'services/TemplateService';
import Swal from 'sweetalert2';

export default defineComponent({
	name: 'TemplatesPage',
	setup() {
		const router = useRouter();

		const items = ref<TemplateModel[]>([]);
		const searchQuery = ref('');
		const currentPage = ref(1);
		const pageSize = ref(25);
		const isLoading = ref(false);
		const showCreateModal = ref(false);
		const newTemplateName = ref('');
		const isCreating = ref(false);

		const filteredItems = computed(() => {
			if (!searchQuery.value) return items.value;
			const query = searchQuery.value.toLowerCase();
			return items.value.filter(item =>
				item.name?.toLowerCase().includes(query) ||
				String(item.id).includes(query)
			);
		});

		const totalRows = computed(() => filteredItems.value.length);
		const totalPages = computed(() => Math.max(1, Math.ceil(totalRows.value / pageSize.value)));
		const startRow = computed(() => totalRows.value === 0 ? 0 : (currentPage.value - 1) * pageSize.value + 1);
		const endRow = computed(() => Math.min(currentPage.value * pageSize.value, totalRows.value));

		const paginatedItems = computed(() => {
			const start = (currentPage.value - 1) * pageSize.value;
			return filteredItems.value.slice(start, start + pageSize.value);
		});

		const goToPage = (page: number) => {
			if (page >= 1 && page <= totalPages.value) {
				currentPage.value = page;
			}
		};

		const loadData = async () => {
			isLoading.value = true;
			try {
				const params = new URLSearchParams({ limit: '0' });
				const data = await TemplateService.getAll(params);
				items.value = data;
			} catch (error: any) {
				console.error('Failed to load templates:', error);
				Swal.fire({
					title: 'Error',
					text: error.message || 'Failed to load templates',
					icon: 'error',
				});
			} finally {
				isLoading.value = false;
			}
		};

		const editItem = (id: number) => {
			router.push(`/template/${id}`);
		};

		const confirmDelete = async (item: TemplateModel) => {
			const result = await Swal.fire({
				title: 'Delete Template',
				text: `Are you sure you want to delete "${item.name}"?`,
				icon: 'warning',
				showCancelButton: true,
				confirmButtonColor: '#dc2626',
				confirmButtonText: 'Delete',
			});

			if (result.isConfirmed) {
				try {
					await TemplateService.delete(item.id);
					await loadData();
					Swal.fire({
						title: 'Deleted',
						text: 'Template has been deleted',
						icon: 'success',
						timer: 1500,
					});
				} catch (error: any) {
					Swal.fire({
						title: 'Error',
						text: error.message || 'Failed to delete template',
						icon: 'error',
					});
				}
			}
		};

		const createItem = async () => {
			if (!newTemplateName.value.trim()) return;

			isCreating.value = true;
			try {
				const payload = {
					name: newTemplateName.value.trim(),
				};

				const newTemplate = await TemplateService.create(payload);
				showCreateModal.value = false;
				newTemplateName.value = '';
				router.push(`/template/${newTemplate.id}`);
			} catch (error: any) {
				Swal.fire({
					title: 'Error',
					text: error.message || 'Failed to create template',
					icon: 'error',
				});
			} finally {
				isCreating.value = false;
			}
		};

		onMounted(async () => {
			await loadData();
		});

		return {
			items, searchQuery, currentPage, pageSize, isLoading, showCreateModal, newTemplateName, isCreating,
			filteredItems, totalRows, totalPages, startRow, endRow, paginatedItems,
			goToPage, loadData, editItem, confirmDelete, createItem,
		};
	},
});
</script>
